import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input"
import { visibilityType } from '../views/CatalogueTableView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAccessOptions } from '../others/Filters';
import { apiSwaggerClientSide } from '@/shared/api';
import { Group, KeycloakUser } from '@/sharedTypes/Api';
import { AdminCatalogueView, SelectedSample } from '../Catalogue';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import toast from '@/components/ui/custom-toast';
import { Settings, ClipboardList, Users, Music, Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';

interface IAccessControlPanelView {
  selectedSamples: SelectedSample[];
  setSelectedSamples: React.Dispatch<React.SetStateAction<SelectedSample[]>>;
  setView: React.Dispatch<React.SetStateAction<AdminCatalogueView>>;
}

const AccessControlPanelView = ({ selectedSamples, setSelectedSamples, setView }: IAccessControlPanelView) => {
  const { t } = useTranslation();
  const ACCESS_OPTIONS = getAccessOptions(t);
  const initialVisibility = selectedSamples[0].sample.isPrivate ?
  'hidden' :
  (selectedSamples[0].sample.accessControl.allowedGroups.length > 0 || selectedSamples[0].sample.accessControl.allowedUsers.length > 0) ? 'restricted' : 'public';

  const [newVisibility, setNewVisibility] = useState<visibilityType>(initialVisibility);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [allowedGroups, setAllowedGroups] = useState<{ _id: string; name: string }[]>([]);
  const [allowedUsers, setAllowedUsers] = useState<{ sub: string; username: string }[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<KeycloakUser[]>([]);
  const [searchedGroups, setSearchedGroups] = useState<Group[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  // Track existing items marked for removal per sample: Map<sampleId, Set<groupId/userId>>
  const [removedGroups, setRemovedGroups] = useState<Map<string, Set<string>>>(new Map());
  const [removedUsers, setRemovedUsers] = useState<Map<string, Set<string>>>(new Map());

  // Debounced user search
  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setSearchedUsers([]);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        setIsDataLoading(true);
        const res = await apiSwaggerClientSide.admin.usersSearchList({ q: userSearchQuery });
        if (res.data.data?.length) {
          setSearchedUsers(res.data.data);
        } else {
          setSearchedUsers([]);
        }
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsDataLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [userSearchQuery]);

  // Debounced group search
  useEffect(() => {
    if (groupSearchQuery.length < 2) {
      setSearchedGroups([]);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        const res = await apiSwaggerClientSide.admin.groupsSearchList({ q: groupSearchQuery });
        if (res.data?.success && res.data.data) {
          setSearchedGroups(res.data.data);
        } else {
          setSearchedGroups([]);
        }
      } catch (error) {
        console.error("Error searching groups:", error);
        setSearchedGroups([]);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [groupSearchQuery]);

  // ** Mock removing all the users and groups(as well as new ones) from selected samples when visibility is changed to 'public'
  // * If hidden -> no changes needed
  useEffect(() => {
    if(newVisibility !== 'public') return;

    const groupsToRemove = new Map<string, Set<string>>();
    const usersToRemove = new Map<string, Set<string>>();

    selectedSamples.forEach(sample => {
      const groupSet = new Set<string>();
      sample.sample.accessControl.allowedGroups.forEach(group => {
        groupSet.add(group._id);
      });
      if (groupSet.size > 0) {
        groupsToRemove.set(sample.id, groupSet);
      }

      const userSet = new Set<string>();
      sample.sample.accessControl.allowedUsers.forEach(user => {
        userSet.add(user.sub);
      });
      if (userSet.size > 0) {
        usersToRemove.set(sample.id, userSet);
      }
    });

    // Remove new users and groups as well
    setAllowedGroups([]);
    setAllowedUsers([]);

    setRemovedGroups(groupsToRemove);
    setRemovedUsers(usersToRemove);

    // If afterwards set back to restricted/hidden, clear the removals
    return () => {
      setRemovedGroups(new Map());
      setRemovedUsers(new Map());
    }
  }, [newVisibility])

  const handleSave = async () => {
    try {
      setIsDataLoading(true);
      const isPrivate = newVisibility === 'hidden';
      const groups = allowedGroups.map(g => g._id);
      const users = allowedUsers.map(u => u.sub);

      // Process each sample individually to handle per-sample removals
      const promises = selectedSamples.map(sample => {
        const groupsToRemove = Array.from(removedGroups.get(sample.id) || []);
        const usersToRemove = Array.from(removedUsers.get(sample.id) || []);

        return apiSwaggerClientSide.admin.samplesBulkCreate({
          sampleIds: [sample.id],
          groups,
          isPrivate,
          users,
          groupsToRemove,
          usersToRemove
        });
      });

      await Promise.all(promises);

      toast({
        title: t('adminCatalogue.accessControl.accessControlChangesSaved'),
        description: t('adminCatalogue.accessControl.accessControlChangesDesc'),
        type: 'success'
      });
      // Clear selections after successful save so fresh data is fetched on re-select
      setSelectedSamples([]);
    } catch(err) {
      console.error("Error saving access control changes:", err);
      toast({
        title: t('adminCatalogue.accessControl.errorSavingChanges'),
        description: t('adminCatalogue.accessControl.errorSavingChangesDesc'),
        type: 'error'
      });
    } finally {
      setIsDataLoading(false);
      goBack();
    }
  }

  // Toggle removal of existing group for a specific sample
  const toggleRemoveGroup = (sampleId: string, groupId: string) => {
    setRemovedGroups(prev => {
      const next = new Map(prev);
      const sampleSet = new Set(next.get(sampleId) || []);
      if (sampleSet.has(groupId)) {
        sampleSet.delete(groupId);
      } else {
        sampleSet.add(groupId);
      }
      if (sampleSet.size === 0) {
        next.delete(sampleId);
      } else {
        next.set(sampleId, sampleSet);
      }
      return next;
    });
  };

  // Toggle removal of existing user for a specific sample
  const toggleRemoveUser = (sampleId: string, userId: string) => {
    setRemovedUsers(prev => {
      const next = new Map(prev);
      const sampleSet = new Set(next.get(sampleId) || []);
      if (sampleSet.has(userId)) {
        sampleSet.delete(userId);
      } else {
        sampleSet.add(userId);
      }
      if (sampleSet.size === 0) {
        next.delete(sampleId);
      } else {
        next.set(sampleId, sampleSet);
      }
      return next;
    });
  };

  // Check if a group is marked for removal for a specific sample
  const isGroupMarkedForRemoval = (sampleId: string, groupId: string) => {
    return removedGroups.get(sampleId)?.has(groupId) || false;
  };

  // Check if a user is marked for removal for a specific sample
  const isUserMarkedForRemoval = (sampleId: string, userId: string) => {
    return removedUsers.get(sampleId)?.has(userId) || false;
  };

  // Remove newly added group
  const removeNewGroup = (groupId: string) => {
    setAllowedGroups(prev => prev.filter(g => g._id !== groupId));
  };

  // Remove newly added user
  const removeNewUser = (userSub: string) => {
    setAllowedUsers(prev => prev.filter(u => u.sub !== userSub));
  };

  const handleCancel = () => {
    // Reset states
    setNewVisibility(initialVisibility);
    setAllowedGroups([]);
    setAllowedUsers([]);
    setSearchedGroups([]);
    setSearchedUsers([]);
    setGroupSearchQuery('');
    setUserSearchQuery('');
    setRemovedGroups(new Map());
    setRemovedUsers(new Map());
    setIsDataLoading(false);
  }

  const goBack = () => {
    handleCancel();
    setSelectedSamples([]);
    setView('CATALOGUE');
  }

  return (
    <div className='flex flex-col h-full' data-testid="access-control-view">
      <div className="flex gap-6 h-full flex-1 min-h-0">
        {/* Left Configuration Panel */}
        <div 
        className='flex flex-col h-full w-[35%] border rounded-lg p-4 overflow-hidden'
        data-testid='access-control-config-panel'
        >
          <div className="flex items-center gap-2 text-gray-700 mb-4">
            <Settings className="h-5 w-5" />
            <span className="font-semibold">{t('adminCatalogue.accessControl.configuration')}</span>
          </div>

          {/* Visibility */}
          <div className="flex flex-col gap-2 w-full mb-4" data-testid='access-control-visibility-setting'>
            <label className="text-sm font-medium text-gray-700">{t('adminCatalogue.accessControl.visibility')}</label>
            <Select
              value={newVisibility}
              onValueChange={(value) => setNewVisibility(value as visibilityType)}
            >
              <SelectTrigger className="w-full bg-white" data-testid="access-control-visibility-dropdown">
                <SelectValue placeholder="Access type" />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.value === 'public' && <Eye className="h-4 w-4 text-green-600" />}
                      {option.value === 'restricted' && <Lock className="h-4 w-4 text-orange-600" />}
                      {option.value === 'hidden' && <EyeOff className="h-4 w-4 text-gray-600" />}
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {newVisibility === 'restricted' && (
            <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
              {/* Add Groups */}
              <div className="flex flex-col gap-2 border rounded-lg p-3" data-testid='access-control-add-groups'>
                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="h-4 w-4" />
                  <label className="text-sm font-medium">{t('adminCatalogue.accessControl.addGroups')}</label>
                </div>
                <Input
                  type="text"
                  placeholder={t('adminCatalogue.accessControl.searchGroups')}
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                  data-testid="access-control-group-search"
                />
                {searchedGroups.filter(group => !allowedGroups.some(allowedGroup => allowedGroup._id === group._id)).length > 0 && (
                  <div className="border rounded-lg bg-white max-h-32 overflow-y-auto" data-testid="access-control-group-results">
                    {searchedGroups.filter(group => !allowedGroups.some(allowedGroup => allowedGroup._id === group._id)).map((group: Group) => (
                      <button
                        key={group._id}
                        data-testid={`access-control-group-result-${group._id}`}
                        className='px-3 py-2 hover:bg-blue-50 cursor-pointer bg-transparent w-full border-0 text-left text-sm flex items-center gap-2 transition-colors'
                        onClick={() => {
                          const _id = group._id;
                          const name = group.name;
                          if (_id && name) {
                            setAllowedGroups((prev) => [...prev, { _id, name }]);
                          }
                          setGroupSearchQuery('');
                        }}
                      >
                        <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-3 w-3 text-blue-700" />
                        </div>
                        {group.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Users */}
              <div className="flex flex-col gap-2 border rounded-lg p-3" data-testid='access-control-add-users'>
                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="h-4 w-4" />
                  <label className="text-sm font-medium">{t('adminCatalogue.accessControl.addUsers')}</label>
                </div>
                <Input
                  type="text"
                  placeholder={t('adminCatalogue.accessControl.searchUsers')}
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  data-testid="access-control-user-search"
                />
                {searchedUsers.length > 0 ? (
                  <div className='border rounded-lg bg-white max-h-32 overflow-y-auto' data-testid="access-control-user-results">
                    {searchedUsers.filter(user => !allowedUsers.some(allowedUser => allowedUser.sub === user.sub)).map((user: KeycloakUser) => (
                      <button
                        key={user.sub}
                        data-testid={`access-control-user-result-${user.sub}`}
                        className='px-3 py-2 hover:bg-green-50 cursor-pointer bg-transparent w-full border-0 text-left text-sm flex items-center gap-2 transition-colors'
                        onClick={() => {
                          const sub = user.sub;
                          const username = user.username;
                          if (sub && username) {
                            setAllowedUsers((prev) => [...prev, { sub, username }]);
                          }
                          setUserSearchQuery('');
                        }}
                      >
                        <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs">
                          {user.username?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-gray-900">{user.username}</span>
                          {user.email && <span className="text-xs text-gray-500">{user.email}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : userSearchQuery.length >= 2 && (
                  <div className='text-sm text-gray-500 text-center py-2'>
                    {isDataLoading ? t('adminCatalogue.common.searching') : t('adminCatalogue.accessControl.noUsersFound')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Right Review Changes Panel */}
        <div 
        className='flex flex-col h-full w-[65%] border rounded-lg p-4 overflow-hidden'
        data-testid='access-control-review-panel'
        >
          <div className="flex items-center gap-2 text-gray-700 mb-4">
            <ClipboardList className="h-5 w-5" />
            <span className="font-semibold">{t('adminCatalogue.accessControl.reviewChanges')} ({selectedSamples.length} {t('adminCatalogue.common.music_sheets')})</span>
          </div>

          <div className='flex-1 overflow-y-auto space-y-3'>
            {selectedSamples.map((item) => (
              <div key={item.id} className='p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors'
              data-testid={`access-control-sample-${item.sample.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-8 w-8 rounded bg-purple-100 flex items-center justify-center">
                    <Music className="h-4 w-4 text-purple-700" />
                  </div>
                  <h4 className='text-sm font-semibold text-gray-900'>{item.sample.title}</h4>
                </div>

                {/* Groups */}
                <div className='mb-3'>
                  <div className="flex items-center gap-1 mb-1">
                    <Users className="h-3 w-3 text-gray-500" />
                    <span className='text-xs font-medium text-gray-500'>{t('adminCatalogue.accessControl.groups')}</span>
                  </div>
                  {item.sample.accessControl.allowedGroups.length > 0 ? (
                    <div className='flex flex-wrap gap-1'>
                      {item.sample.accessControl.allowedGroups.map((group) => {
                        const isMarkedForRemoval = isGroupMarkedForRemoval(item.id, group._id);
                        return (
                          <button
                            key={group._id}
                            data-testid={`access-control-existing-group-${group._id}`}
                            onClick={() => toggleRemoveGroup(item.id, group._id)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer border-0 transition-colors ${
                              isMarkedForRemoval
                                ? 'bg-red-100 text-red-700 line-through hover:bg-red-200'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            {group.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className='text-xs text-gray-400 italic'>{t('adminCatalogue.accessControl.noneAssigned')}</span>
                  )}
                </div>

                {/* Users */}
                <div className='mb-3'>
                  <div className="flex items-center gap-1 mb-1">
                    <Users className="h-3 w-3 text-gray-500" />
                    <span className='text-xs font-medium text-gray-500'>{t('adminCatalogue.accessControl.users')}</span>
                  </div>
                  {item.sample.accessControl.allowedUsers.length > 0 ? (
                    <div className='flex flex-wrap gap-1'>
                      {item.sample.accessControl.allowedUsers.map((user) => {
                        const isMarkedForRemoval = isUserMarkedForRemoval(item.id, user.sub);
                        return (
                          <button
                            key={user.sub}
                            data-testid={`access-control-existing-user-${user.sub}`}
                            onClick={() => toggleRemoveUser(item.id, user.sub)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer border-0 transition-colors ${
                              isMarkedForRemoval
                                ? 'bg-red-100 text-red-700 line-through hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {user.username}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className='text-xs text-gray-400 italic'>{t('adminCatalogue.accessControl.noneAssigned')}</span>
                  )}
                </div>

                {/* New Groups */}
                {allowedGroups.length > 0 && (
                  <div className='mb-2 pt-2 border-t'>
                    <div className="flex items-center gap-1 mb-1">
                      <span className='text-xs font-medium text-blue-600'>{t('adminCatalogue.accessControl.newGroups')}</span>
                    </div>
                    <div className='flex flex-wrap gap-1'>
                      {allowedGroups.map((group) => (
                        <button
                          key={group._id}
                          data-testid={`access-control-new-group-${group._id}`}
                          onClick={() => removeNewGroup(group._id)}
                          className='px-2.5 py-1 text-xs font-medium bg-blue-200 text-blue-800 rounded-full cursor-pointer border-0 hover:bg-red-100 hover:text-red-700 transition-colors'
                        >
                          {group.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Users */}
                {allowedUsers.length > 0 && (
                  <div className='pt-2 border-t'>
                    <div className="flex items-center gap-1 mb-1">
                      <span className='text-xs font-medium text-green-600'>{t('adminCatalogue.accessControl.newUsers')}</span>
                    </div>
                    <div className='flex flex-wrap gap-1'>
                      {allowedUsers.map((user) => (
                        <button
                          key={user.sub}
                          data-testid={`access-control-new-user-${user.sub}`}
                          onClick={() => removeNewUser(user.sub)}
                          className='px-2.5 py-1 text-xs font-medium bg-green-200 text-green-800 rounded-full cursor-pointer border-0 hover:bg-red-100 hover:text-red-700 transition-colors'
                        >
                          {user.username}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='flex pt-4 mt-4 justify-between border-t'>
        <ButtonShadcn
          variant="outline"
          onClick={goBack}
          data-testid="access-control-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('adminCatalogue.accessControl.backToCatalogue')}
        </ButtonShadcn>
        <div className='flex gap-3'>
          <ButtonShadcn
            variant="outline"
            onClick={handleCancel}
            data-testid="access-control-reset-btn"
          >
            {t('adminCatalogue.accessControl.reset')}
          </ButtonShadcn>
          <ButtonShadcn
            variant="default"
            onClick={handleSave}
            disabled={isDataLoading}
            data-testid="access-control-save-btn"
          >
            {isDataLoading ? t('adminCatalogue.common.saving') : t('adminCatalogue.accessControl.saveChanges')}
          </ButtonShadcn>
        </div>
      </div>
    </div>
  )
}

export default AccessControlPanelView